"use client";

import { Button } from "@/components/ui/button";
import {
  Menu,
  ChevronDown,
  Award,
  School,
  GraduationCap,
  ClipboardCheck,
  Bot,
} from "lucide-react";
import { useState } from "react";
import Image from "next/image";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n } from "@/lib/i18n/LanguageProvider";
import { withUtm } from "@/lib/utm";
import { track, trackAndOpen } from "@/lib/tracking";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [productsOpenMobile, setProductsOpenMobile] = useState(false);
  const { t } = useI18n();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
      setIsMenuOpen(false);
    }
  };

  const utmOpts = { medium: "menu", campaign: "header_dropdown" };
  const freeBadgeStyle = "bg-primary/10 text-primary";
  const freemiumBadgeStyle = "bg-amber-100 text-amber-700";
  const productItems = [
    {
      name: "IELTS Guru",
      caption: t.nav.productItems.ieltsGuruCaption,
      href: withUtm("https://ieltsguru.ai/", utmOpts),
      Icon: Award,
      iconBg: "bg-indigo-100 text-indigo-600",
      badge: t.nav.freeBadge,
      badgeStyle: freeBadgeStyle,
    },
    {
      name: "Trường Số",
      caption: t.nav.productItems.truongSoCaption,
      href: withUtm("https://truongso.net", utmOpts),
      Icon: School,
      iconBg: "bg-emerald-100 text-emerald-600",
      badge: t.nav.freeBadge,
      badgeStyle: freeBadgeStyle,
    },
    {
      name: "FLYER LMS",
      caption: t.nav.productItems.flyerLmsCaption,
      href: withUtm("https://lms.flyer.vn", utmOpts),
      Icon: GraduationCap,
      iconBg: "bg-violet-100 text-violet-600",
      badge: t.nav.freeBadge,
      badgeStyle: freeBadgeStyle,
    },
    {
      name: "FLYER Test Prep",
      caption: t.nav.productItems.flyerTestPrepCaption,
      href: withUtm("https://exam.flyer.us/en", utmOpts),
      Icon: ClipboardCheck,
      iconBg: "bg-orange-100 text-orange-600",
      badge: t.nav.freemiumBadge,
      badgeStyle: freemiumBadgeStyle,
    },
    {
      name: "Bingo",
      caption: t.nav.productItems.bingoCaption,
      href: withUtm("https://study.flyer.us/?selectedLevel=starters", utmOpts),
      Icon: Bot,
      iconBg: "bg-pink-100 text-pink-600",
      badge: t.nav.freemiumBadge,
      badgeStyle: freemiumBadgeStyle,
    },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <div className="container mx-auto px-4 md:px-8 py-4">
        <nav className="flex items-center justify-between gap-4">
          <div className="flex items-center -ml-2 md:ml-0">
            <Image
              src="/assets/flyer-teacher-logo.png"
              alt="Teado Logo"
              width={48}
              height={48}
              className="rounded-lg"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-foreground hover:text-primary transition-colors font-medium py-2"
                aria-expanded={productsOpen}
                aria-haspopup="menu"
              >
                {t.nav.products}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${productsOpen ? "rotate-180" : ""}`}
                />
              </button>
              {productsOpen && (
                <div
                  role="menu"
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[360px]"
                >
                  <div className="bg-background border border-border rounded-xl shadow-lg p-2">
                    {productItems.map((p) => (
                      <a
                        key={p.name}
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        role="menuitem"
                        onClick={() =>
                          track("cta_click", {
                            cta_label: p.name,
                            cta_location: "header_products_menu",
                            target_url: p.href,
                          })
                        }
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors"
                      >
                        <div
                          className={`${p.iconBg} rounded-lg p-2 flex-shrink-0`}
                        >
                          <p.Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground">
                              {p.name}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.badgeStyle}`}
                            >
                              {p.badge}
                            </span>
                          </div>
                          <p className="text-sm text-foreground/70 mt-0.5 leading-snug">
                            {p.caption}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => scrollToSection("partners")}
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              {t.nav.partners}
            </button>
            <Button
              onClick={() =>
                trackAndOpen(t.links.teacherHeader, {
                  ctaLabel: t.nav.tryFree,
                  ctaLocation: "header",
                  linkType: "signup",
                })
              }
              className="bg-foreground text-background hover:bg-foreground/90 transition-opacity"
            >
              {t.nav.tryFree}
            </Button>
            <LanguageSwitcher />
          </div>

          {/* Mobile CTA Button and Menu */}
          <div className="md:hidden flex items-center gap-2">
            <Button
              onClick={() =>
                trackAndOpen(t.links.teacherHeader, {
                  ctaLabel: t.nav.tryFree,
                  ctaLocation: "header_mobile",
                  linkType: "signup",
                })
              }
              size="sm"
              className="bg-foreground text-background hover:bg-foreground/90 transition-opacity text-xs px-3 py-1 h-8"
            >
              {t.nav.tryFree}
            </Button>
            <LanguageSwitcher className="px-2 py-1" />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Menu">
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 flex flex-col gap-2">
            <button
              onClick={() => setProductsOpenMobile(!productsOpenMobile)}
              className="flex items-center justify-between text-left text-foreground hover:text-primary transition-colors font-medium py-2"
              aria-expanded={productsOpenMobile}
            >
              {t.nav.products}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${productsOpenMobile ? "rotate-180" : ""}`}
              />
            </button>
            {productsOpenMobile && (
              <div className="flex flex-col gap-1 pl-2 pb-2">
                {productItems.map((p) => (
                  <a
                    key={p.name}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      track("cta_click", {
                        cta_label: p.name,
                        cta_location: "header_products_menu_mobile",
                        target_url: p.href,
                      })
                    }
                    className="flex items-start gap-3 p-2 rounded-lg hover:bg-secondary/60 transition-colors"
                  >
                    <div className={`${p.iconBg} rounded-lg p-2 flex-shrink-0`}>
                      <p.Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground text-sm">
                          {p.name}
                        </span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${p.badgeStyle}`}
                        >
                          {p.badge}
                        </span>
                      </div>
                      <p className="text-xs text-foreground/70 mt-0.5 leading-snug">
                        {p.caption}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            )}
            <button
              onClick={() => scrollToSection("partners")}
              className="text-left text-foreground hover:text-primary transition-colors font-medium py-2"
            >
              {t.nav.partners}
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
